import React from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Award } from 'lucide-react';
import { openWhatsAppQuote } from '../utils/whatsapp';

const CoconutNet: React.FC = () => {
  const features = [
    "Coconut tree protection systems",
    "Fruit safety nets for coconut trees",
    "Agricultural protection solutions",
    "Weather resistance for farms",
    "Farm safety and crop protection",
    "Easy installation in agricultural areas",
    "Durable agricultural-grade materials",
    "Professional installation service"
  ];

  const benefits = [
    "Protects coconut trees and fruits",
    "Prevents fruit damage from birds",
    "Agricultural crop protection",
    "Weather and UV resistant",
    "Easy to install and maintain",
    "Cost-effective farm solution",
    "Durable and long-lasting",
    "Professional installation guarantee"
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Sports Nets Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover opacity-50"
            onError={(e) => {
              console.log('Video failed to load, using fallback image');
              const fallbackDiv = document.createElement('div');
              fallbackDiv.className = 'absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat';
              fallbackDiv.style.backgroundImage = `url('/images/garden.webp')`;
              if (e.currentTarget.parentNode) {
                e.currentTarget.parentNode.appendChild(fallbackDiv);
                e.currentTarget.style.display = 'none';
              }
            }}
            onLoadStart={() => {
              console.log('Attempting to load sports-nets video...');
            }}
            onCanPlay={() => {
              console.log('Video loaded successfully and can play');
            }}
            onLoadedData={(e) => {
              console.log('Video data loaded');
              e.currentTarget.play().catch(err => console.log('Auto-play failed:', err));
            }}
          >
            <source src="/videos/sports-nets.mp4" type="video/mp4" />
            <source src="/videos/sports-nets.webm" type="video/webm" />
          </video>
        </div>
        
        {/* Dark Overlay for Better Text Visibility */}
        <div className="absolute inset-0 bg-black opacity-25"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-20 w-20 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Coconut Net
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto font-medium">
            Protective nets for coconut trees and fruit protection. 
            Keep your coconut trees and fruits safe with our professional agricultural net solutions.
          </p>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Coconut Nets in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional coconut net installations protecting trees and fruits from damage and theft.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow"
            >
              <img
                src="/images/garden.webp"
                alt="Coconut tree protection net"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                <p className="text-white font-semibold text-lg">Tree Protection</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow"
            >
              <img
                src="/images/garden.webp"
                alt="Fruit protection nets"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                <p className="text-white font-semibold text-lg">Fruit Protection</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow"
            >
              <img
                src="/images/garden.webp"
                alt="Agricultural safety nets"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                <p className="text-white font-semibold text-lg">Agricultural Safety</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Coconut Net Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive agricultural protection solutions designed specifically for coconut trees and fruit protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-700 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Coconut Nets?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our coconut nets provide maximum agricultural protection with professional installation and ongoing support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <Shield className="h-16 w-16 text-blue-500 mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Agricultural Protection</h3>
              <ul className="space-y-3">
                {benefits.slice(0, 4).map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <Award className="h-16 w-16 text-blue-500 mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Professional Service</h3>
              <ul className="space-y-3">
                {benefits.slice(4).map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Protect Your Coconut Trees?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Get a free consultation and quote for coconut nets. 
            Serving Andhra Pradesh and Telangana with professional installation.
          </p>
          <button
            onClick={() => openWhatsAppQuote()}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors inline-flex items-center"
          >
            Get Free Quote
          </button>
        </div>
      </section>
    </div>
  );
};

export default CoconutNet;
